import React, { useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { AgGrid, Flex, Text, Button } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { MAX_VISIBLE_CHIPS } from "./constants";
import * as styles from "./styles_vanilla.css";
import type { ClientRowItem } from "./ClientsView/ClientsTable/types";
import type { IndividualRowItem } from "./IndividualsView/IndividualsTable/types";

type WorkflowTypeCount = { name: string; value: string; count: number };

type BuildNavigationUrl<T> = (data: T, type: WorkflowTypeCount) => string | null;

type WorkflowTypeChipsProps<T extends ClientRowItem | IndividualRowItem> = {
  value?: unknown;
  data?: T;
  api?: AgGrid.GridApi;
  node?: AgGrid.IRowNode;
  buildNavigationUrl: BuildNavigationUrl<T>;
};

export const WorkflowTypeChips = <T extends ClientRowItem | IndividualRowItem>({
  value,
  data,
  api,
  node,
  buildNavigationUrl,
}: WorkflowTypeChipsProps<T>): React.ReactElement | null => {
  const history = useHistory();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleChipClick = useCallback(
    (e: React.MouseEvent, type: WorkflowTypeCount) => {
      e.stopPropagation();
      if (!data) return;
      const url = buildNavigationUrl(data, type);
      if (!url) return;
      history.push(url);
    },
    [data, buildNavigationUrl, history],
  );

  const types: WorkflowTypeCount[] = Array.isArray(value) ? value : (data?.workflowTypes ?? []);

  if (!types || types.length === 0) {
    return (
      <Text variant="regular-sm" color={Colors.Neutral500}>
        —
      </Text>
    );
  }

  const displayedTypes = isExpanded ? types : types.slice(0, MAX_VISIBLE_CHIPS);
  const hiddenCount = types.length - MAX_VISIBLE_CHIPS;

  const handleExpandToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
    if (api && node) {
      api.onRowHeightChanged();
    }
  };

  return (
    <Flex className={styles.chipsContainer} gap="SM" flexWrap="wrap" alignItems="center">
      {displayedTypes.map((type) => (
        <button
          key={type.value}
          className={styles.chip}
          onClick={(e) => handleChipClick(e, type)}
          title={`View ${type.count} ${type.name} workflows`}
          aria-label={`View ${type.count} ${type.name} workflows`}
          type="button"
          data-testid={`workflow-type-chip-${type.value}`}
        >
          <span className={styles.chipText}>{type.name}</span>
          <span className={styles.chipCount}>{type.count}</span>
        </button>
      ))}
      {hiddenCount > 0 && (
        <Button
          className={styles.moreChip}
          onClick={handleExpandToggle}
          variant="neutral"
          title={isExpanded ? "Show less" : "Show all workflow types"}
        >
          {isExpanded ? "Show less" : `+${hiddenCount} more`}
        </Button>
      )}
    </Flex>
  );
};
